// lib
import React, { useReducer, useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import { dataReducer } from '@aytm/app/stats/page/question-views/cq/lib/reducers'
import { checkError } from '@aytm/app/stats/page/question-views/cq/lib/validate'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// components
import { default as ComponentContainer } from '@aytm/app/stats/page/question-views/components/questions-custom/Component'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import Batch from '@aytm/app/stats/page/question-views/cq/components/Batch'

import Controls from '@aytm/app/stats/page/question-views/cq/components/Controls'
import Footer from '@aytm/app/stats/page/question-views/components/questions-custom/Footer'

// styles
import style from '@aytm/app/stats/page/question-views/cq/styles/Component.module.scss'

export const DataContext = React.createContext()

const Component = ({ model, data, states, disabled, onChange, onControlsChange, onUpdateData }) => {
  const [state, dispatch] = useReducer(dataReducer, {
    batches: JSON.parse(JSON.stringify(model.batches)),
    logicData: { items: {} },
    errors: []
  })

  const [changed, setChanged] = useState(false)

  const timer = useRef(null)

  const stateRef = useRef()
  stateRef.current = state

  const checkModel = () => {
    const newData = {}
    if (!isEqual(state.batches, model.batches)) {
      newData.logicData = state.logicData
      newData.errors = JSON.parse(JSON.stringify(state.errors))
      for (const bi in state.batches) {
        delete newData.logicData.items[`b${Number(bi) + 1}`]
        const index = newData.errors.indexOf(`b${Number(bi) + 1}`)
        0 <= index && newData.errors.splice(index, 1)
      }
      newData.batches = JSON.parse(JSON.stringify(model.batches))
    }
    return newData
  }
  const checkState = () => {
    const newData = {}
    if (!isEqual(state.batches, model.batches)) newData.batches = JSON.parse(JSON.stringify(state.batches))
    return newData
  }

  const onReverse = () => {
    dispatch({
      type: 'REVERSE_BATCHES'
    })
  }

  useEffect(() => {
    if (changed) return

    dispatch({
      type: 'RESET',
      data: { ...checkModel() }
    })

    setChanged(!isEqual(stateRef.current.batches, model.batches))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.batches])

  useEffect(() => {
    setChanged(true)

    const newData = {
      logicData: state.logicData,
      ...checkError(state),
      ...checkState()
    }

    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      onChange(newData)

      setChanged(false)
    }, 300)

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const dataContextValue = useMemo(() => {
    return {
      data,
      disabled,
      dispatch
    }
  }, [data, disabled, dispatch])

  return (
    <BlockWrapper>
      <ComponentContainer
        Controls={Controls}
        model={model}
        data={data}
        states={states}
        changed={changed}
        disabled={disabled}
        onChange={onControlsChange}
      >
        <div className={style.component} data-testid="stat-dl-cq-form">
          <div className={style.row}>
            <div className={`${style.label} p-col-5`}>
              <b>1. Choose a banner</b> - answers to a certain question, traits
            </div>
            <div className={`${style.label} p-col-5`}>
              <b>2. Choose an attribute</b> to check for correlations
            </div>
          </div>
          <div className={`${style.list} ${style.row}`}>
            <DataContext.Provider value={dataContextValue}>
              <div className={`${style.column} ${style['column-av']} p-col-5`}>
                <Batch item={state.batches[0]} position={1} />
              </div>
              <div className={`${style.column} p-col`}>
                <span
                  data-testid="stat-dl-cq-swap"
                  className={style.toggle}
                  aria-label="Swap banner and attribute"
                  {...pressable(onReverse, { disabled })}
                >
                  <span />
                  <span />
                  <span />
                </span>
              </div>
              <div className={`${style.column} ${style['column-av']} p-col-5`}>
                <Batch item={state.batches[1]} position={2} />
              </div>
            </DataContext.Provider>
          </div>
        </div>
        {!disabled && <Footer model={model} states={states} onUpdateData={onUpdateData} />}
      </ComponentContainer>
    </BlockWrapper>
  )
}

Component.propTypes = {
  model: PropTypes.object,
  data: PropTypes.object,
  states: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onControlsChange: PropTypes.func,
  onUpdateData: PropTypes.func
}

Component.displayName = 'Component'

export default Component
